import { type ComponentType, useState } from 'react';
import { Link, useMatch } from '@tanstack/react-router';
import { useGroups, isActiveGroup, type Group, type GroupType } from '@entities/group';
import { useSession } from '@entities/session';
import { Amount, Skeleton } from '@ui';
import {
  GroupTypeFriends,
  GroupTypeHome,
  GroupTypeOffice,
  GroupTypeOthers,
  GroupTypeRoommates,
  GroupTypeTrip,
} from '@ui/icons';
import { cn, type Money } from '@shared/lib';
import { t } from '@shared/i18n';
import NoGroupsIllustration from '@shared/assets/Images/no-groups.svg?react';

type GroupTab = 'active' | 'archived' | 'closed' | 'all';

const TABS: { value: GroupTab; label: string }[] = [
  { value: 'active', label: t.myGroups.tabs.active },
  { value: 'archived', label: t.myGroups.tabs.archived },
  { value: 'closed', label: t.myGroups.tabs.closed },
  { value: 'all', label: t.myGroups.tabs.all },
];

const TYPE_ICONS: Record<GroupType, ComponentType<{ className?: string }>> = {
  home: GroupTypeHome,
  trip: GroupTypeTrip,
  friends: GroupTypeFriends,
  office: GroupTypeOffice,
  roommates: GroupTypeRoommates,
  others: GroupTypeOthers,
};

/** Filter the full group list to the selected tab using the backend status flags. */
function filterByTab(groups: Group[], tab: GroupTab): Group[] {
  switch (tab) {
    case 'archived':
      return groups.filter((g) => g.isArchived);
    case 'closed':
      return groups.filter((g) => g.isClosed);
    case 'all':
      return groups;
    default: // active = neither archived nor closed
      return groups.filter(isActiveGroup);
  }
}

/**
 * Derives the overall status text for a group (matches mobile `_buildOverallText` logic):
 * 1. Waiting for first expense if no shared expenses exist & balance is non-negative
 * 2. Overall, You Are Settled if balance is 0
 * 3. Overall, You Will Receive if balance is negative (yourBalance < 0)
 * 4. Overall, You need to pay if balance is positive (yourBalance > 0)
 */
function buildOverallText(group: Group): string {
  const isReceive = group.yourBalance < 0;
  const isNonNegative = group.yourBalance >= 0;
  const totalSharedAmount = group.sharedAmount;

  if ((totalSharedAmount === 0 || !group.hasExpenses) && isNonNegative) {
    return t.myGroups.waiting;
  }
  if (group.yourBalance === 0) {
    return t.groupDetail.overallSettled;
  }
  if (isReceive) {
    return t.groupDetail.overallReceive;
  }
  return t.groupDetail.overallPay;
}

/** Persistent "My Groups" rail (Figma) — status tabs + the user's groups per status. */
export function ConversationRail() {
  const { data: groups, isPending } = useGroups();
  const { data: user } = useSession();
  const [tab, setTab] = useState<GroupTab>('active');
  // Whether we're already viewing a group's page (the rail is also mounted there). Switching
  // between groups from here is a lateral move — Back should return to Dashboard, not walk
  // through every group visited along the way — so REPLACE the current history entry instead of
  // pushing a new one, but only while already on a group page. Opening the FIRST group from the
  // Dashboard is a genuine drill-down and still pushes, so Back from it correctly returns there.
  const groupRouteMatch = useMatch({ from: '/_authenticated/groups/$groupId', shouldThrow: false });
  const onGroupPage = Boolean(groupRouteMatch);

  const userId = user?.id;
  const list = filterByTab(groups ?? [], tab);

  return (
    <nav
      aria-label={t.myGroups.title}
      className="flex h-full w-full flex-col overflow-hidden rounded-[20px] border border-border bg-[#FFFFFF]/50"
    >
      <div className="flex flex-col gap-5 px-5 pt-5">
        <h2 className="text-base font-semibold font-sans text-fg-strong">{t.myGroups.title}</h2>
        <div className="flex flex-wrap gap-2">
          {TABS.map((item) => {
            const active = item.value === tab;
            return (
              <button
                key={item.value}
                type="button"
                onClick={() => setTab(item.value)}
                className={cn(
                  'cursor-pointer rounded-full px-4 py-2 text-sm/3.5 tracking-[-0.28px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  active
                    ? 'bg-primary font-medium text-primary-fg'
                    : 'border border-border-soft bg-surface-raised text-fg/70 hover:text-fg',
                )}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      </div>

      {!isPending && list.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center py-10">
          <NoGroupsIllustration aria-hidden className="size-15 w-auto" />
          <p className="text-sm font-bold text-primary">{t.myGroups.empty}</p>
        </div>
      ) : (
        <ul className="mt-3 min-h-0 flex-1 overflow-y-auto">
          {isPending ? (
            <RailSkeleton />
          ) : (
            list.map((group) => {
              const Icon = TYPE_ICONS[group.type];
              // Backend sign: a positive balance means the user OWES (Liability); a negative
              // balance means the user is owed (Receivable).
              const owe = group.yourBalance > 0;
              const owed = group.yourBalance < 0;
              // Settlements that involve the current user (the "For You" view).
              const myLines = userId
                ? group.settlements.filter((s) => s.fromId === userId || s.toId === userId)
                : [];

              return (
                <li key={group.id}>
                  <Link
                    to="/groups/$groupId"
                    params={{ groupId: group.id }}
                    // Don't preload the group-detail route on hover — its loader eagerly fetches
                    // `/groups/:id/expenses`, which spammed the network from the rail. The data
                    // loads on actual navigation instead.
                    preload={false}
                    // See `onGroupPage` above — replace while already on a group page, push from
                    // Dashboard.
                    replace={onGroupPage}
                    // Open group → highlighted background + a green left indicator (inset shadow,
                    // so it adds no layout shift). TanStack Router appends activeProps.className.
                    className="flex items-start justify-between gap-2 border-b border-b-brand-30 px-5 py-4 transition-colors hover:bg-black/2"
                    activeProps={{
                      className: 'bg-[#fff] shadow-[inset_3px_0_0_0_#87D906]',
                    }}
                  >
                    <span className="flex min-w-0 flex-1 gap-2">
                      <Icon className="size-7 shrink-0 rounded-md" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-base font-normal font-sans text-fg">
                          {group.name}
                        </span>
                        {myLines.length > 0 ? (
                          <ul className="mt-1.5 ml-4 list-disc space-y-1 text-xs text-[#747474] marker:text-[#747474]">
                            {myLines.map((s, i) => {
                              const pay = s.fromId === userId;
                              return (
                                <li key={i}>
                                  {pay ? t.myGroups.payTo : t.myGroups.receiveFrom}{' '}
                                  {pay ? s.toName : s.fromName}{' '}
                                  <Amount
                                    value={s.amount}
                                    currency={group.currency}
                                    tone={pay ? 'negative' : 'positive'}
                                    weight="normal"
                                  />
                                </li>
                              );
                            })}
                          </ul>
                        ) : (
                          <span className="mt-1.5 block text-xs text-muted">
                            {buildOverallText(group)}
                          </span>
                        )}
                      </span>
                    </span>

                    {owed || owe ? (
                      <span className="flex shrink-0 flex-col items-end">
                        <span className={cn('text-2xs', owed ? 'text-positive' : 'text-negative')}>
                          {owed ? t.myGroups.receivable : t.myGroups.liability}
                        </span>
                        <Amount
                          value={Math.abs(group.yourBalance) as Money}
                          currency={group.currency}
                          tone={owed ? 'positive' : 'negative'}
                          weight="semibold"
                          className="text-lg"
                        />
                      </span>
                    ) : (
                      <span className="shrink-0 text-[8px] text-fg">-</span>
                    )}
                  </Link>
                </li>
              );
            })
          )}
        </ul>
      )}
    </nav>
  );
}

function RailSkeleton() {
  return (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <li key={i} className="flex items-start gap-2 border-b border-brand-30 px-5 py-4">
          <Skeleton className="size-7 shrink-0 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-4/5" />
          </div>
        </li>
      ))}
    </>
  );
}
